import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { isDesktop, isMobile } from '@direct-frontend/platform-code';
import { genericForwardRef, IsNever, useMountEffect } from '@direct-frontend/stdlib';
import { HoverEvents } from '@react-types/shared';
import {
    AriaRole,
    ComponentType,
    CSSProperties,
    ForwardedRef,
    forwardRef,
    FunctionComponent,
    HTMLAttributeAnchorTarget,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mergeProps, useHover, usePress } from 'react-aria';

import type { RouterProps } from '../../../internal/types/routerTypes';
import { useInternalVerticalAlignWithSkin } from '../../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { ColorlessIconProps, IconOpenInNewTab } from '../../../icons';
import { EmptyIcon } from '../../../icons/EmptyIcon';
import { TestProps } from '../../../internal/types/testTypes';
import { cn } from '../../../lib/css/className/className';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { useTheme } from '../../../lib/theme/ThemeProvider';
import { HelpHandler, HelpProps } from '../../Help/types';
import { LabelProps } from '../../Label/Label';
import { HStack, VStack } from '../../Stack';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { Link } from '../../Link';
import { ListBaseItem, ListBaseItemProps } from '../ListBaseItem/ListBaseItem';
import { ListIconRender } from '../ListIconRender/ListIconRender';
import { getTextPresetWithSkin, ListContext } from '../utils';

import { useListItemSearch } from './useListItemSearch';
import './ListItem.css';

const cls = cn('ListItem');

export type ListItemContentProps =
    | {
          /** Содержимое элемента */
          children: string;
          /**
           * Текстовое представление элемента для поиска.
           * Для текстового содержимого можно не передавать — поиск идёт по самому содержимому.
           * Если задано, при поиске элемент показывается именно этим текстом с подсветкой совпадения.
           * `null` исключает элемент из поиска: при поиске он не показывается никогда
           */
          textValue?: string | null;
      }
    | {
          /** Содержимое элемента */
          children: ReactNode;
          /**
           * Текстовое представление элемента для поиска.
           * При поиске элемент показывается этим текстом с подсветкой совпадения вместо содержимого.
           * `null` исключает элемент из поиска: при поиске он не показывается никогда
           */
          textValue: string | null;
      };

export interface WithoutIdProps {
    /**
     * Действие при нажатии на элемент. Если не пусто, то элемент становится интерактивным и поддерживает ховер
     * @returns void
     */
    onClick?: () => void;
}

export interface WithIdProps<IdType extends string = string> {
    /**
     * id элемента, будет передан в обработчик клика
     * @example `<ListItem id="a" onClick={id => console.log(id)} />`
     */
    id: IdType;
    /**
     * Действие при нажатии на элемент. Если не пусто, то элемент становится интерактивным и поддерживает ховер
     * @returns void
     */
    onClick?: (id: IdType) => void;
}

const preventDefault = (e: SyntheticEvent) => e.preventDefault();

export type ListItemProps<IdType extends string = never> = Omit<ListBaseItemProps, 'children'> &
    HelpHandler &
    TestProps &
    IsNever<IdType, WithoutIdProps, WithIdProps<IdType>> &
    HoverEvents &
    RouterProps &
    Loggable &
    ListItemContentProps & {
        /**
         * Дополнительная подпись к содержимому элемента
         */
        caption?: ReactNode;

        /**
         * Флаг отключенности элемента.
         * Отвечает за цвет текста, дефолтный цвет иконки и возможность взаимодействия с элементом
         */
        isDisabled?: boolean;

        /**
         * Иконка, которую нужно отрисовать слева от основного контента. При отрисовке передается нужный размер и цвет
         * @example <ListItem iconLeft={IconAttach}>content</ListItem>
         * @example <ListItem iconLeft={props => <IconErrorFilled {...props} />}>content</ListItem>
         */
        iconLeft?: ComponentType<ColorlessIconProps>;

        /**
         * Флаг для резервации места под иконку слева
         */
        iconLeftReserveSpace?: boolean;

        /**
         * Иконка, которую нужно отрисовать справа от основного контента. При отрисовке передается нужный размер и цвет
         * @example <ListItem iconRight={IconAttach}>content</ListItem>
         * @example <ListItem iconRight={props => <IconErrorFilled {...props} />}>content</ListItem>
         */
        iconRight?: ComponentType<ColorlessIconProps>;

        /**
         * Флаг для резервации места под иконку справа
         */
        iconRightReserveSpace?: boolean;

        /**
         * !!Для иконок используйте iconLeft!!
         * Кастомное содержимое слева от основного контента, имеет приоритет над `iconLeft`
         * @example <ListItem addonLeft={<Image width={40} height={40} alt="image" />}>content</ListItem>
         */
        addonLeft?: ReactNode;

        /**
         * !!Для иконок используйте iconRight!!
         * Кастомное содержимое справа от основного контента
         * @default иконка открытия в новой вкладке для ссылок
         */
        addonRight?: ReactNode;

        /**
         * Лейбл, рисующийся справа от основного контента
         * @example <ListItem label={props => <Label {...props}>новинка</Label>}>content</ListItem>
         */
        label?: FunctionComponent<LabelProps>;

        /**
         * Флаг, отвечающий за обрезку текста в элементе.
         * Если `true`, то текст не переносится и обрезается с многоточием
         */
        isTruncated?: boolean;

        /**
         * Адрес, на который нужно перейти при нажатии на элемент. Превращает элемент в нативную ссылку
         * @example <ListItem href="https://ya.ru">follow link</ListItem>
         */
        href?: string;

        /**
         * Атрибут target для ссылки
         */
        target?: HTMLAttributeAnchorTarget;

        /**
         * Роль элемента. По умолчанию используется `listitem`, но для использования в Select лучше передать `option`.
         * @default listitem
         */
        role?: AriaRole;

        /**
         * Стили для использования drag and drop
         */
        draggableStyle?: CSSProperties;
    };

/**
 * Компонент для одиночного элемента списка. Может быть интерактивным
 */
export const ListItem = genericForwardRef(function ListItem<T extends string = never>(
    props: ListItemProps<T>,
    forwardedRef: ForwardedRef<HTMLLIElement>,
) {
    const {
        children,
        href,
        routerOptions,
        draggableStyle,
        target,
        isDisabled,
        caption,
        isTruncated,
        help,
        iconLeft: IconLeft,
        iconLeftReserveSpace,
        iconRight,
        iconRightReserveSpace,
        addonLeft: propsAddonLeft,
        addonRight: propsAddonRight,
        label,
        className,
        role = 'listitem',
        'data-testid': dataTestId = 'ListItem',
    } = props;

    const contextValue = useContext(ListContext);
    const { size, onItemClick } = contextValue;
    const ref = useRef(null);
    const { LogProvider, logClick } = useLogger('ListItem', props.logNode);

    // Выключаем клик при первом рендере чтобы клик не отрабатывал на новом открытом меню на мобилке
    const [isFirstRender, setIsFirstRender] = useState(true);
    useMountEffect(() => setIsFirstRender(false));

    const isLink = Boolean(href) && !isDisabled;
    const isInteractive =
        Boolean(props.onClick) ||
        Boolean(href) ||
        Boolean(props.onHoverStart) ||
        Boolean(props.onHoverEnd) ||
        Boolean(props.onHoverChange);
    const isInteractionDisabled = isDisabled || !isInteractive || (isLink && isFirstRender);

    const handleClick = useMemo(
        () => ('id' in props ? () => props.onClick?.(props.id) : () => props.onClick?.()),
        [props],
    );
    const onClick = useCallback(() => {
        logClick?.();
        onItemClick?.();
        handleClick();
    }, [logClick, onItemClick, handleClick]);

    const { pressProps, isPressed } = usePress({
        onPress: onClick,
        isDisabled: isInteractionDisabled,
    });

    const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false });
    const textPreset = getTextPresetWithSkin(props);
    const { addonClasses: labelClass } = useInternalVerticalAlignWithSkin({
        textSize: textPreset,
        addonType: 'icon',
    });
    const addonLeft = useMemo(() => {
        if (propsAddonLeft) {
            return propsAddonLeft;
        }

        if (IconLeft || iconLeftReserveSpace) {
            return <ListIconRender icon={IconLeft ?? EmptyIcon} isDisabled={isDisabled} />;
        }

        return null;
    }, [IconLeft, iconLeftReserveSpace, isDisabled, propsAddonLeft]);
    const addonRight = useMemo(() => {
        if (propsAddonRight) {
            return propsAddonRight;
        }

        if (iconRight || iconRightReserveSpace) {
            return <ListIconRender icon={iconRight ?? EmptyIcon} isDisabled={isDisabled} />;
        }

        if (href && target === '_blank') {
            return (
                <ListIconRender
                    icon={IconOpenInNewTab}
                    role="supplementary"
                    className={cls('new-tab', { visible: isMobile() || (isHovered && !isDisabled) })}
                />
            );
        }

        return null;
    }, [href, iconRight, iconRightReserveSpace, isDisabled, isHovered, propsAddonRight, target]);

    const { innerLabelSize } = useTheme().sizeTokens[size];
    const renderedLabel = useMemo(() => label?.({ size: innerLabelSize }), [innerLabelSize, label]);
    const colorSkinClass = getColorSkin(isDesktop() ? 'gray-transparent' : 'gray-neutral', {
        isHovered: !isInteractionDisabled && (isPressed || isHovered),
        isDisabled,
    });
    const { isSearchActive, isSearchMatch, highlightedChildren } = useListItemSearch(children, props.textValue);

    // Клик по хелпу не должен вызывать переход по ссылке
    const wrappedHelp = useMemo(() => {
        if (!help) {
            return help;
        }

        return function WrappedHelp(props: HelpProps) {
            return (
                <span className={cls('help')} onClickCapture={preventDefault}>
                    {help?.(props)}
                </span>
            );
        };
    }, [help]);

    let content = (
        <TextGenericWithSkin
            className={cls('content')}
            textSize={textPreset}
            skinSize={size}
            caption={caption}
            color="current"
            isTruncated={isTruncated}
            help={wrappedHelp}
        >
            {isSearchActive ? highlightedChildren : children}
        </TextGenericWithSkin>
    );

    if (addonLeft || addonRight || label) {
        content = (
            <HStack gap={'current-geometry'} alignItems="flex-start" flexWrap="nowrap">
                {addonLeft}
                {content}
                {renderedLabel && <VStack className={labelClass}>{renderedLabel}</VStack>}
                {addonRight}
            </HStack>
        );
    }

    const LinkElement = useMemo(
        () =>
            forwardRef<HTMLAnchorElement>(function LinkElement(props, ref) {
                return <a ref={ref} {...mergeProps(hoverProps, props, { onClick })} role={role} />;
            }),
        [hoverProps, onClick, role],
    );

    if (isSearchActive && !isSearchMatch) {
        return null;
    }

    if (isInteractive) {
        if (isLink) {
            content = (
                <Link
                    isDisabled={isFirstRender}
                    elementType={LinkElement}
                    href={href}
                    target={target}
                    routerOptions={routerOptions}
                    className={cls('link')}
                    ref={ref}
                    data-testid={dataTestId}
                >
                    {content}
                </Link>
            );
        } else {
            content = (
                <div {...mergeProps(pressProps, hoverProps)} role={role} data-testid={dataTestId}>
                    {content}
                </div>
            );
        }
    }

    return (
        <LogProvider>
            <ListBaseItem
                className={cls(
                    {
                        hovered: isHovered,
                        pressed: isPressed,
                        disabled: isInteractionDisabled,
                        size,
                        'has-addon-left': Boolean(addonLeft),
                        'has-addon-right': Boolean(addonRight) || Boolean(renderedLabel),
                    },
                    [className, colorSkinClass],
                )}
                data-testid={isInteractive ? `${dataTestId}.BaseItem` : dataTestId}
                isDisabled={isDisabled}
                role={isInteractive ? 'none' : role}
                ref={forwardedRef}
                draggableStyle={draggableStyle}
            >
                {content}
            </ListBaseItem>
        </LogProvider>
    );
});
